import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Plus, FileText, UtensilsCrossed, MessageCircle } from 'lucide-react';
import { toast } from 'sonner';
import Layout from '../components/Layout';
import ProgressChart from '../components/ProgressChart';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PacienteDetalhes = () => {
  const { patientId } = useParams();
  const navigate = useNavigate();
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showPrescriptionForm, setShowPrescriptionForm] = useState(false);
  const [showMealPlanForm, setShowMealPlanForm] = useState(false);
  const [showNoteForm, setShowNoteForm] = useState(false);
  
  const [prescriptionData, setPrescriptionData] = useState({
    medicamento: '',
    dosagem: '',
    horarios: '',
    duracao_dias: '',
    observacoes: ''
  });

  const [noteData, setNoteData] = useState('');

  useEffect(() => {
    fetchPatientSummary();
  }, [patientId]);

  const fetchPatientSummary = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/doctor/patients/${patientId}/summary`);
      setSummary(response.data);
    } catch (error) {
      console.error('Erro ao carregar dados:', error);
      toast.error('Erro ao carregar dados do paciente');
    } finally {
      setLoading(false);
    }
  };

  const handleCreatePrescription = async (e) => {
    e.preventDefault();
    try {
      const data = {
        patient_id: patientId,
        medicamento: prescriptionData.medicamento,
        dosagem: prescriptionData.dosagem,
        horarios: prescriptionData.horarios.split(',').map(h => h.trim()),
        duracao_dias: parseInt(prescriptionData.duracao_dias),
        observacoes: prescriptionData.observacoes
      };
      await axios.post(`${API_URL}/api/prescriptions`, data);
      toast.success('Prescrição criada com sucesso!');
      setShowPrescriptionForm(false);
      setPrescriptionData({
        medicamento: '',
        dosagem: '',
        horarios: '',
        duracao_dias: '',
        observacoes: ''
      });
      fetchPatientSummary();
    } catch (error) {
      console.error('Erro ao criar prescrição:', error);
      toast.error('Erro ao criar prescrição');
    }
  };

  const handleCreateNote = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API_URL}/api/doctor/clinical-notes`, {
        patient_id: patientId,
        nota: noteData
      });
      toast.success('Nota clínica adicionada!');
      setShowNoteForm(false);
      setNoteData('');
    } catch (error) {
      console.error('Erro ao criar nota:', error);
      toast.error('Erro ao criar nota');
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="text-muted-foreground">Carregando...</div>
        </div>
      </Layout>
    );
  }

  if (!summary) {
    return (
      <Layout>
        <div className="text-center py-12">
          <p className="text-muted-foreground">Paciente não encontrado</p>
        </div>
      </Layout>
    );
  }

  const pesoData = summary.recent_progress
    .filter(p => p.peso)
    .map(p => ({ data: p.data, peso: p.peso }))
    .reverse();

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <button
            data-testid="back-button"
            onClick={() => navigate('/medico/dashboard')}
            className="p-2 hover:bg-muted rounded-lg transition-colors"
          >
            <ArrowLeft size={24} />
          </button>
          <div className="flex-1">
            <h2 className="text-3xl font-bold" style={{ fontFamily: 'Manrope, sans-serif' }}>
              {summary.patient.nome}
            </h2>
            <p className="text-muted-foreground" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
              {summary.patient.email}
            </p>
          </div>
          <button
            onClick={() => navigate(`/chat/${patientId}`)}
            className="bg-secondary text-secondary-foreground rounded-full px-6 py-3 font-bold hover:shadow-lg transition-all flex items-center gap-2"
          >
            <MessageCircle size={20} />
            Chat
          </button>
        </div>

        {/* Patient Info */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-card rounded-2xl shadow-sm border border-border p-6">
            <p className="text-sm text-muted-foreground mb-2">Peso Inicial</p>
            <p className="text-2xl font-bold" style={{ fontFamily: 'Manrope, sans-serif' }}>
              {summary.patient.peso_inicial || '--'} kg
            </p>
          </div>
          <div className="bg-card rounded-2xl shadow-sm border border-border p-6">
            <p className="text-sm text-muted-foreground mb-2">Meta</p>
            <p className="text-2xl font-bold text-primary" style={{ fontFamily: 'Manrope, sans-serif' }}>
              {summary.patient.peso_meta || '--'} kg
            </p>
          </div>
          <div className="bg-card rounded-2xl shadow-sm border border-border p-6">
            <p className="text-sm text-muted-foreground mb-2">Altura</p>
            <p className="text-2xl font-bold" style={{ fontFamily: 'Manrope, sans-serif' }}>
              {summary.patient.altura || '--'} m
            </p>
          </div>
        </div>

        {/* Progress Chart */}
        {pesoData.length > 0 && (
          <ProgressChart
            data={pesoData}
            dataKey="peso"
            title="Evolução do Peso"
            color="#2D6A4F"
          />
        )}

        {/* Actions */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <button
            data-testid="create-prescription-button"
            onClick={() => setShowPrescriptionForm(!showPrescriptionForm)}
            className="bg-primary text-primary-foreground rounded-2xl p-6 text-left hover:shadow-lg transition-shadow"
          >
            <FileText size={32} className="mb-3" />
            <h3 className="text-lg font-bold" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Nova Prescrição
            </h3>
          </button>

          <button
            data-testid="create-mealplan-button"
            onClick={() => toast.info('Funcionalidade em desenvolvimento')}
            className="bg-secondary text-secondary-foreground rounded-2xl p-6 text-left hover:shadow-lg transition-shadow"
          >
            <UtensilsCrossed size={32} className="mb-3" />
            <h3 className="text-lg font-bold" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Plano Alimentar
            </h3>
          </button>

          <button
            data-testid="create-note-button"
            onClick={() => setShowNoteForm(!showNoteForm)}
            className="bg-accent/20 text-accent rounded-2xl p-6 text-left hover:shadow-lg transition-shadow"
          >
            <Plus size={32} className="mb-3" />
            <h3 className="text-lg font-bold" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Nota Clínica
            </h3>
          </button>
        </div>

        {/* Prescription Form */}
        {showPrescriptionForm && (
          <div className="bg-card rounded-2xl shadow-sm border border-border p-6">
            <h3 className="text-lg font-semibold mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Nova Prescrição
            </h3>
            <form onSubmit={handleCreatePrescription} className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">Medicamento</label>
                <input
                  data-testid="input-medicamento"
                  type="text"
                  value={prescriptionData.medicamento}
                  onChange={(e) => setPrescriptionData({ ...prescriptionData, medicamento: e.target.value })}
                  className="w-full bg-muted border-transparent focus:border-primary focus:ring-2 focus:ring-primary/20 rounded-xl h-12 px-4"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Dosagem</label>
                <input
                  data-testid="input-dosagem"
                  type="text"
                  value={prescriptionData.dosagem}
                  onChange={(e) => setPrescriptionData({ ...prescriptionData, dosagem: e.target.value })}
                  className="w-full bg-muted border-transparent focus:border-primary focus:ring-2 focus:ring-primary/20 rounded-xl h-12 px-4"
                  placeholder="10mg"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Horários (separados por vírgula)</label>
                <input
                  data-testid="input-horarios"
                  type="text"
                  value={prescriptionData.horarios}
                  onChange={(e) => setPrescriptionData({ ...prescriptionData, horarios: e.target.value })}
                  className="w-full bg-muted border-transparent focus:border-primary focus:ring-2 focus:ring-primary/20 rounded-xl h-12 px-4"
                  placeholder="08:00, 20:00"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Duração (dias)</label>
                <input
                  data-testid="input-duracao"
                  type="number"
                  value={prescriptionData.duracao_dias}
                  onChange={(e) => setPrescriptionData({ ...prescriptionData, duracao_dias: e.target.value })}
                  className="w-full bg-muted border-transparent focus:border-primary focus:ring-2 focus:ring-primary/20 rounded-xl h-12 px-4"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Observações</label>
                <textarea
                  data-testid="input-observacoes"
                  value={prescriptionData.observacoes}
                  onChange={(e) => setPrescriptionData({ ...prescriptionData, observacoes: e.target.value })}
                  className="w-full bg-muted border-transparent focus:border-primary focus:ring-2 focus:ring-primary/20 rounded-xl p-4"
                  rows={3}
                />
              </div>
              <div className="flex gap-3">
                <button
                  data-testid="submit-prescription"
                  type="submit"
                  className="flex-1 bg-primary text-primary-foreground rounded-full py-3 font-bold"
                >
                  Criar Prescrição
                </button>
                <button
                  type="button"
                  onClick={() => setShowPrescriptionForm(false)}
                  className="flex-1 bg-muted text-muted-foreground rounded-full py-3 font-bold"
                >
                  Cancelar
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Note Form */}
        {showNoteForm && (
          <div className="bg-card rounded-2xl shadow-sm border border-border p-6">
            <h3 className="text-lg font-semibold mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Nova Nota Clínica
            </h3>
            <form onSubmit={handleCreateNote} className="space-y-4">
              <textarea
                data-testid="input-nota"
                value={noteData}
                onChange={(e) => setNoteData(e.target.value)}
                className="w-full bg-muted border-transparent focus:border-primary focus:ring-2 focus:ring-primary/20 rounded-xl p-4"
                rows={5}
                placeholder="Digite sua nota clínica..."
                required
              />
              <div className="flex gap-3">
                <button
                  data-testid="submit-note"
                  type="submit"
                  className="flex-1 bg-primary text-primary-foreground rounded-full py-3 font-bold"
                >
                  Salvar Nota
                </button>
                <button
                  type="button"
                  onClick={() => setShowNoteForm(false)}
                  className="flex-1 bg-muted text-muted-foreground rounded-full py-3 font-bold"
                >
                  Cancelar
                </button>
              </div>
            </form>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default PacienteDetalhes;